import { useState, useEffect, useCallback } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import AdminModal from '../../components/admin/AdminModal';
import Badge from '../../components/admin/Badge';
import StatCard from '../../components/admin/StatCard';
import Pagination from '../../components/admin/Pagination';

const STAGES = [
  { key: 'all', label: 'All' },
  { key: 'registered', label: 'Registered' },
  { key: 'verified', label: 'Verified' },
  { key: 'checkout_started', label: 'Checkout' },
  { key: 'payment_initiated', label: 'Payment' },
  { key: 'subscribed', label: 'Subscribed' },
];

const stageBadgeVariant = {
  registered: 'pending',
  verified: 'info',
  checkout_started: 'warning',
  payment_initiated: 'warning',
  subscribed: 'active',
};

function AdminCustomers({ apiFetch, toast }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadUsers = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 25 });
      if (search) params.set('search', search);
      if (verifiedFilter !== '') params.set('email_verified', verifiedFilter);
      const res = await apiFetch(`/api/admin/customers?${params}`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
    } catch { toast.error('Error', 'Failed to load customers'); }
    finally { setLoading(false); }
  }, [apiFetch, page, search, verifiedFilter, toast]);

  const loadFunnel = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/customers/funnel');
      if (res.ok) setFunnel(await res.json());
    } catch { /* ignore */ }
  }, [apiFetch]);

  useEffect(() => { loadUsers(1); }, [search, verifiedFilter]);
  useEffect(() => { loadFunnel(); }, [loadFunnel]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const openDetail = async (user) => {
    setDetailLoading(true);
    setDetail({ user });
    try {
      const res = await apiFetch(`/api/admin/customers/${user.id}`);
      if (res.ok) setDetail(await res.json());
      else throw new Error();
    } catch { toast.error('Error', 'Failed to load user details'); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  // Derive filtered list based on stage filter
  const filteredUsers = stageFilter === 'all' ? users : users.filter(u => u.funnel_stage === stageFilter);

  const funnelSteps = funnel ? [
    { key: 'registered', label: 'Registered', count: funnel.total_registered },
    { key: 'verified', label: 'Verified', count: funnel.total_verified },
    { key: 'checkout', label: 'Checkout', count: funnel.total_checkout_started },
    { key: 'payment', label: 'Payment', count: funnel.total_payment_initiated },
    { key: 'subscribed', label: 'Subscribed', count: funnel.total_subscribed },
  ] : [];

  const columns = [
    { key: 'email', header: 'Email', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true, render: row => row.full_name || '—' },
    { key: 'funnel_stage', header: 'Stage', render: row => (
      <Badge variant={stageBadgeVariant[row.funnel_stage] || 'info'}>{row.funnel_stage.replace('_', ' ')}</Badge>
    )},
    { key: 'email_verified', header: 'Verified', render: row => (
      <Badge variant={row.email_verified ? 'active' : 'pending'}>{row.email_verified ? 'Yes' : 'No'}</Badge>
    )},
    { key: 'stripe_customer_id', header: 'Stripe', cellStyle: { fontSize: '12px' }, render: row => row.stripe_customer_id
      ? <span style={{ color: 'var(--admin-primary)', fontFamily: 'monospace', fontSize: '11px' }}>{row.stripe_customer_id.slice(0, 18)}…</span>
      : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>
    },
    { key: 'utm_source', header: 'Source', cellStyle: { fontSize: '12px' }, render: row => row.utm_source
      ? <span title={[row.utm_source, row.utm_medium, row.utm_campaign, row.utm_content].filter(Boolean).join(' / ')}>
          <Badge variant="info">{row.utm_source}</Badge>
          {row.utm_content ? <span style={{ color: 'var(--admin-text-muted)', marginLeft: '4px' }}>{row.utm_content}</span> : null}
        </span>
      : <span style={{ color: 'var(--admin-text-muted)' }}>—</span>
    },
    { key: 'sub_count', header: 'Subs', sortable: true, render: row => row.sub_count || 0 },
    { key: 'last_login', header: 'Last Login', cellStyle: { color: 'var(--admin-text-muted)', fontSize: '12px' }, sortable: true,
      render: row => row.last_login ? new Date(row.last_login).toLocaleDateString() : 'Never' },
    { key: 'created_at', header: 'Registered', cellStyle: { color: 'var(--admin-text-muted)', fontSize: '12px' }, sortable: true,
      render: row => new Date(row.created_at).toLocaleDateString() },
  ];

  return (
    <>
      <SectionHeader title="Customers" description="All registered portal users and signup funnel" />

      {/* Funnel Bar */}
      {funnel && (
        <div className="admin-card" style={{ marginBottom: '20px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px' }}>
            {funnelSteps.map((step, i) => {
              const maxCount = funnelSteps[0].count || 1;
              const pct = Math.max(((step.count / maxCount) * 100), 5);
              const prevCount = i > 0 ? funnelSteps[i - 1].count : step.count;
              const dropoff = prevCount > 0 ? Math.round(((prevCount - step.count) / prevCount) * 100) : 0;
              return (
                <div key={step.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--admin-text-muted)', fontWeight: 500 }}>
                    {step.label}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 700 }}>{step.count}</div>
                  <div style={{
                    width: '100%', height: `${pct}%`, minHeight: '4px',
                    background: `var(--admin-primary)`, borderRadius: '4px', opacity: 0.3 + (0.7 * (pct / 100)),
                  }} />
                  {i > 0 && dropoff > 0 && (
                    <div style={{ fontSize: '10px', color: 'var(--admin-error)', fontWeight: 600 }}>
                      -{dropoff}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="admin-stats-grid" style={{ marginBottom: '20px' }}>
        <StatCard label="Total Customers" value={funnel?.total_registered ?? '—'} icon="fa-solid fa-users" />
        <StatCard label="Conversion Rate" value={funnel ? `${funnel.conversion_rate}%` : '—'} icon="fa-solid fa-chart-line" variant={funnel?.conversion_rate > 5 ? 'success' : undefined} />
        <StatCard label="New (7d)" value={funnel?.new_last_7d ?? '—'} icon="fa-solid fa-user-plus" />
        <StatCard label="New (30d)" value={funnel?.new_last_30d ?? '—'} icon="fa-solid fa-calendar" />
      </div>

      {/* Filter Bar */}
      <div className="admin-card" style={{ marginBottom: '16px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '6px', flex: 1, minWidth: '200px' }}>
          <input
            className="admin-input"
            placeholder="Search email or name…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ flex: 1, fontSize: '13px' }}
          />
          <button type="submit" className="admin-btn admin-btn--sm admin-btn--primary">
            <i className="fa-solid fa-search" />
          </button>
          {search && (
            <button type="button" className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => { setSearch(''); setSearchInput(''); }}>
              <i className="fa-solid fa-times" />
            </button>
          )}
        </form>

        <div style={{ display: 'flex', gap: '4px' }}>
          {STAGES.map(s => (
            <button
              key={s.key}
              className={`admin-btn admin-btn--sm ${stageFilter === s.key ? 'admin-btn--primary' : 'admin-btn--ghost'}`}
              onClick={() => setStageFilter(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <select
          className="admin-input"
          value={verifiedFilter}
          onChange={e => setVerifiedFilter(e.target.value)}
          style={{ width: 'auto', fontSize: '13px' }}
        >
          <option value="">All verified</option>
          <option value="1">Verified only</option>
          <option value="0">Unverified only</option>
        </select>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        onRowClick={openDetail}
        emptyMessage="No customers found"
        emptyIcon="fa-solid fa-address-book"
      />

      <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: 'var(--admin-text-muted)' }}>
        <span>{total} total customer{total !== 1 ? 's' : ''}</span>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={(p) => { setPage(p); loadUsers(p); }} />

      {/* Detail Modal */}
      {detail && (
        <AdminModal
          title={detail.user?.email || 'Customer Detail'}
          onClose={() => setDetail(null)}
          width="700px"
        >
          {detailLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--admin-text-muted)' }}>Loading…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* User Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                <div><strong>Name:</strong> {detail.user?.full_name || '—'}</div>
                <div><strong>Email:</strong> {detail.user?.email}</div>
                <div><strong>Verified:</strong> <Badge variant={detail.user?.email_verified ? 'active' : 'pending'}>{detail.user?.email_verified ? 'Yes' : 'No'}</Badge></div>
                <div><strong>Stripe ID:</strong> {detail.user?.stripe_customer_id || '—'}</div>
                <div><strong>Registered:</strong> {detail.user?.created_at ? new Date(detail.user.created_at).toLocaleString() : '—'}</div>
                <div><strong>Last Login:</strong> {detail.user?.last_login ? new Date(detail.user.last_login).toLocaleString() : 'Never'}</div>
              </div>

              {/* Marketing Attribution — where this customer signed up from */}
              {(detail.user?.utm_source || detail.user?.utm_medium || detail.user?.utm_campaign || detail.user?.utm_content || detail.user?.landing_page || detail.user?.referrer_url) && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Signed up from (attribution)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                    <div><strong>Source:</strong> {detail.user.utm_source || '—'}</div>
                    <div><strong>Section (medium):</strong> {detail.user.utm_medium || '—'}</div>
                    <div><strong>Page (campaign):</strong> {detail.user.utm_campaign || '—'}</div>
                    <div><strong>CTA (content):</strong> {detail.user.utm_content || '—'}</div>
                    <div><strong>Landing page:</strong> {detail.user.landing_page || '—'}</div>
                    <div><strong>Referrer:</strong> {detail.user.referrer_url ? <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>{detail.user.referrer_url}</span> : '—'}</div>
                  </div>
                </div>
              )}

              {/* Stripe Subscriptions */}
              {detail.stripeSubscriptions && detail.stripeSubscriptions.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Stripe Subscriptions</h4>
                  <div className="admin-table-wrapper">
                    <table className="admin-table" style={{ fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Plan</th>
                          <th>Status</th>
                          <th>Amount</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.stripeSubscriptions.map(sub => (
                          <tr key={sub.id}>
                            <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{sub.id.slice(0, 20)}…</td>
                            <td>{sub.plan_slug || '—'}</td>
                            <td>
                              <Badge variant={sub.status === 'active' ? 'active' : sub.status === 'trialing' ? 'info' : 'suspended'}>
                                {sub.status}
                              </Badge>
                            </td>
                            <td>{sub.amount != null ? `${(sub.amount / 100).toFixed(2)} ${(sub.currency || 'usd').toUpperCase()}/${sub.interval}` : '—'}</td>
                            <td>{new Date(sub.created * 1000).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Checkout Activity */}
              {detail.checkoutSessions && detail.checkoutSessions.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Checkout Activity</h4>
                  <div className="admin-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="admin-table" style={{ fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Domain</th>
                          <th>Completed</th>
                          <th>Payment</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.checkoutSessions.map(cs => (
                          <tr key={cs.id}>
                            <td>{cs.plan_name || cs.plan_slug}</td>
                            <td style={{ fontSize: '11px' }}>{cs.domain || cs.subdomain || '—'}</td>
                            <td><Badge variant={cs.completed ? 'active' : 'pending'}>{cs.completed ? 'Yes' : 'No'}</Badge></td>
                            <td>
                              {cs.payment_intent_id ? <Badge variant="active">Paid</Badge> :
                               cs.setup_intent_id ? <Badge variant="info">Setup</Badge> :
                               <Badge variant="pending">—</Badge>}
                            </td>
                            <td>{new Date(cs.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Subscription Prices (local) */}
              {detail.subscriptionPrices && detail.subscriptionPrices.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Local Subscription Records</h4>
                  <div className="admin-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="admin-table" style={{ fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Price</th>
                          <th>Sub ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.subscriptionPrices.map(sp => (
                          <tr key={sp.id || sp.subscription_id}>
                            <td>{sp.plan_slug}</td>
                            <td>{sp.price_at_subscription ? `${sp.price_at_subscription} ${sp.currency || 'USD'}` : '—'}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: '11px' }}>{sp.subscription_id?.slice(0, 20) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </AdminModal>
      )}
    </>
  );
}

export default AdminCustomers;
